import { useState, useEffect } from 'react';
import { TrendingUp, AlertTriangle, Download, ShoppingCart, CreditCard, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { ventasApi, stockApi, busesApi, pagosCreditoApi, descargarReporteDiario } from '../lib/api';
import { ventaPagada } from '../lib/ventas';
import { fechaLima } from '../lib/fechas';

const ReportesCompleto = () => {
  const [ventas, setVentas] = useState<any[]>([]);
  const [productos, setProductos] = useState<any[]>([]);
  const [buses, setBuses] = useState<any[]>([]);
  const [pagosCredito, setPagosCredito] = useState<any[]>([]);
  const [vistaActual, setVistaActual] = useState<'dashboard' | 'ventas' | 'creditos' | 'utilidades'>('dashboard');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [filtroTipoPago, setFiltroTipoPago] = useState('todos');
  const [fechaReporteDiario, setFechaReporteDiario] = useState(() => fechaLima(new Date()));
  const [generandoReporteDiario, setGenerandoReporteDiario] = useState(false);

  // Cargar datos desde Supabase
  useEffect(() => {
    cargarDatos();
    
    // Establecer rango de fechas amplio para mostrar todas las ventas
    const hoy = new Date();
    const hace30Dias = new Date();
    hace30Dias.setDate(hace30Dias.getDate() - 30);
    
    setFechaInicio(fechaLima(hace30Dias));
    setFechaFin(fechaLima(hoy));
  }, []);

  const cargarDatos = async () => {
    try {
      // Las ventas ya traen sus items embebidos (JSONB) desde el backend
      const ventasData = await ventasApi.getAll();
      const ventasOrdenadas = [...(ventasData || [])].sort(
        (a: any, b: any) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
      );
      setVentas(ventasOrdenadas);

      const productosData = await stockApi.getFull();
      setProductos(productosData || []);

      const busesData = await busesApi.getAll();
      setBuses((busesData || []).filter((b: any) => b.estado === 'activo'));

      const pagosData = await pagosCreditoApi.getAll();
      setPagosCredito(pagosData || []);

      console.log('✅ Datos cargados:', {
        ventas: ventasOrdenadas.length,
        productos: productosData?.length
      });
    } catch (error) {
      console.error('Error cargando datos:', error);
    }
  };

  // Filtrar ventas por fecha
  const ventasFiltradas = ventas.filter(venta => {
    if (!venta.fecha) return false;
    
    // Extraer solo la fecha (YYYY-MM-DD) sin hora
    const fechaVentaStr = venta.fecha.split('T')[0];
    const fechaVenta = new Date(fechaVentaStr + 'T00:00:00');
    
    const inicio = fechaInicio ? new Date(fechaInicio + 'T00:00:00') : new Date('1900-01-01'); // hora-lima-ok: solo significa "desde siempre"
    const fin = fechaFin ? new Date(fechaFin + 'T23:59:59') : new Date();

    return fechaVenta >= inicio && fechaVenta <= fin;
  });

  // Pagos de credito (bus a bus) filtrados por el mismo rango de fechas que las ventas.
  const pagosCreditoFiltrados = pagosCredito.filter(pago => {
    if (!pago.fecha) return false;
    const fechaPagoStr = pago.fecha.split('T')[0];
    const fechaPago = new Date(fechaPagoStr + 'T00:00:00');
    const inicio = fechaInicio ? new Date(fechaInicio + 'T00:00:00') : new Date('1900-01-01'); // hora-lima-ok: solo significa "desde siempre"
    const fin = fechaFin ? new Date(fechaFin + 'T23:59:59') : new Date();
    return fechaPago >= inicio && fechaPago <= fin;
  });
  const totalCobradoPeriodo = pagosCreditoFiltrados.reduce((sum, p) => sum + parseFloat(p.monto || 0), 0);

  // Deuda pendiente REAL de hoy: buses.credito_usado ya baja con cada pago registrado
  // (a diferencia de venta.estado, que se queda en 'pendiente' para siempre una vez
  // creada la venta a credito). No se filtra por fecha: es un saldo, no un movimiento
  // del periodo.
  const deudaPendienteReal = buses.reduce((sum, b) => sum + parseFloat(b.credito_usado || 0), 0);
  const busesConDeudaReal = buses.filter(b => parseFloat(b.credito_usado || 0) > 0).length;

  console.log('🔍 Debug Reportes:', {
    totalVentas: ventas.length,
    ventasFiltradas: ventasFiltradas.length,
    fechaInicio,
    fechaFin,
    primeraVentaFecha: ventas[0]?.fecha,
    primeraVentaFechaSplit: ventas[0]?.fecha?.split('T')[0]
  });

  // Calcular estadísticas de ventas
  const estadisticasVentas = () => {
    const totalVentas = ventasFiltradas.length;
    // transferencia se cuenta como contado: es un pago inmediato (ya no tiene ni
    // siquiera opcion en la pantalla de Ventas actual, es un tipo heredado de datos
    // viejos), no una deuda como credito/mixto.
    const ventasContado = ventasFiltradas.filter(v => v.tipo_pago === 'efectivo' || v.tipo_pago === 'tarjeta' || v.tipo_pago === 'transferencia');
    const ventasCredito = ventasFiltradas.filter(v => (v.tipo_pago === 'credito' || v.tipo_pago === 'mixto'));
    const ventasYape = ventasFiltradas.filter(v => v.tipo_pago === 'yape');

    const montoTotalVentas = ventasFiltradas.reduce((sum, v) => sum + (v.total || 0), 0);
    const montoContado = ventasContado.reduce((sum, v) => sum + (v.total || 0), 0);
    const montoCredito = ventasCredito.reduce((sum, v) => sum + (v.total || 0), 0);
    const montoYape = ventasYape.reduce((sum, v) => sum + (v.total || 0), 0);

    console.log('💰 Estadísticas calculadas:', {
      totalVentas,
      montoTotalVentas,
      montoContado,
      montoCredito,
      ventasConTotal: ventasFiltradas.map(v => ({ id: v.id, total: v.total, tipo_pago: v.tipo_pago }))
    });

    // Calcular utilidades (precio de venta - costo). El costo se toma del snapshot
    // guardado en el item al momento de la venta (item.precio_compra); si una venta
    // antigua no lo tiene, se cae al precio_compra actual del producto como aproximación.
    let utilidadTotal = 0;
    ventasFiltradas.forEach(venta => {
      if (venta.items && Array.isArray(venta.items)) {
        venta.items.forEach((item: any) => {
          const producto = productos.find(p => p.id === item.id || p.id === item.producto_id);
          const costoUnitario = item.precio_compra ?? producto?.precio_compra ?? 0;
          const precioVenta = item.precio ?? item.precio_unitario ?? 0;
          const cantidad = item.cantidad ?? 0;
          utilidadTotal += (precioVenta - costoUnitario) * cantidad;
        });
      }
    });

    return {
      totalVentas,
      ventasContado: ventasContado.length,
      ventasCredito: ventasCredito.length,
      ventasYape: ventasYape.length,
      montoTotalVentas,
      montoContado,
      montoCredito,
      montoYape,
      utilidadTotal,
      ticketPromedio: totalVentas > 0 ? montoTotalVentas / totalVentas : 0
    };
  };

  // Calcular estadísticas de créditos. El credito es del bus (por padron), no de una
  // persona: se agrupa por placa_bus en vez de por cliente.
  const estadisticasCreditos = () => {
    const ventasCredito = ventasFiltradas.filter(v => (v.tipo_pago === 'credito' || v.tipo_pago === 'mixto'));
    const busesConCredito = [...new Set(ventasCredito.map(v => v.placa_bus).filter(Boolean))];

    let creditosPendientes = 0;
    let creditosVencidos = 0;
    let montoTotalCreditos = 0;
    let montoPendiente = 0;
    let montoVencido = 0;

    const hoy = new Date();
    const DIAS_CREDITO_DEFAULT = 30;

    ventasCredito.forEach(venta => {
      const fechaVencimiento = new Date(venta.fecha);
      fechaVencimiento.setDate(fechaVencimiento.getDate() + DIAS_CREDITO_DEFAULT);

      montoTotalCreditos += venta.total;

      // Lo pendiente es el saldo real de la venta (ventas.estado no cambia al pagarse)
      const v = venta as any;
      const saldo = ((Number(v.saldo_credito_productos) || 0) + (Number(v.saldo_credito_servicios) || 0));
      if (saldo > 0.004) {
        creditosPendientes++;
        montoPendiente += saldo;

        if (fechaVencimiento < hoy) {
          creditosVencidos++;
          montoVencido += saldo;
        }
      }
    });

    return {
      totalCreditos: ventasCredito.length,
      clientesConCredito: busesConCredito.length,
      creditosPendientes,
      creditosVencidos,
      montoTotalCreditos,
      montoPendiente,
      montoVencido,
      porcentajeVencido: montoPendiente > 0 ? (montoVencido / montoPendiente) * 100 : 0
    };
  };

  // Obtener ventas por día para gráfico
  const ventasPorDia = () => {
    const ventasPorFecha: { [key: string]: number } = {};
    
    ventasFiltradas.forEach(venta => {
      const fecha = venta.fecha;
      if (ventasPorFecha[fecha]) {
        ventasPorFecha[fecha] += venta.total;
      } else {
        ventasPorFecha[fecha] = venta.total;
      }
    });

    return Object.entries(ventasPorFecha)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([fecha, monto]) => ({ fecha, monto }));
  };

  // Obtener productos más vendidos
  const productosMasVendidos = () => {
    const productosVentas: { [key: string]: { cantidad: number, monto: number, nombre: string } } = {};
    
    ventasFiltradas.forEach(venta => {
      if (venta.items && Array.isArray(venta.items)) {
        venta.items.forEach((item: any) => {
          const itemId = item.id ?? item.producto_id;
          const precio = item.precio ?? item.precio_unitario ?? 0;
          if (productosVentas[itemId]) {
            productosVentas[itemId].cantidad += item.cantidad;
            productosVentas[itemId].monto += precio * item.cantidad;
          } else {
            const producto = productos.find(p => p.id === itemId);
            productosVentas[itemId] = {
              cantidad: item.cantidad,
              monto: precio * item.cantidad,
              nombre: item.nombre || producto?.nombre || itemId
            };
          }
        });
      }
    });

    return Object.entries(productosVentas)
      .map(([id, data]) => ({ id, ...data }))
      .sort((a, b) => b.monto - a.monto)
      .slice(0, 10);
  };

  // Genera el reporte diario en PDF (ventas, creditos nuevos, repuestos vendidos e
  // ingresos por caja de un dia puntual), lo sube a Google Drive del lado del servidor,
  // y lo descarga -- volver a generar el mismo dia sobreescribe el PDF anterior, tanto
  // local como en Drive, asi que sirve para "actualizar" el reporte si hay ventas nuevas.
  const handleGenerarReporteDiario = async () => {
    setGenerandoReporteDiario(true);
    try {
      await descargarReporteDiario(fechaReporteDiario);
      toast.success(`Reporte diario del ${fechaReporteDiario} generado y guardado en Google Drive`);
    } catch (error) {
      toast.error('No se pudo generar el reporte diario');
    } finally {
      setGenerandoReporteDiario(false);
    }
  };

  // Exportar reporte a CSV (más ligero que Excel)
  const exportarCSV = (tipo: string) => {
    let datos: any[] = [];
    let nombreArchivo = '';
    let headers: string[] = [];

    switch (tipo) {
      case 'ventas':
        headers = ['Número', 'Fecha', 'Cliente', 'Placa Bus', 'Tipo Pago', 'Subtotal', 'Descuento', 'Total', 'Estado'];
        datos = ventasFiltradas.map(venta => [
          venta.numero,
          new Date(venta.fecha).toLocaleDateString('es-PE'),
          venta.cliente_nombre || 'Cliente General',
          venta.placa_bus || 'Sin padrón',
          venta.tipo_pago,
          venta.subtotal.toFixed(2),
          venta.descuento.toFixed(2),
          venta.total.toFixed(2),
          ventaPagada(venta) ? 'Pagado' : 'Pendiente'
        ]);
        nombreArchivo = `reporte_ventas_${fechaInicio}_${fechaFin}.csv`;
        break;

      case 'creditos':
        headers = ['Número', 'Fecha', 'Placa Bus', 'Monto', 'Fecha Vencimiento', 'Días Vencido', 'Estado'];
        const ventasCredito = ventasFiltradas.filter(v => v.tipo_pago === 'credito' || v.tipo_pago === 'mixto');
        datos = ventasCredito.map(venta => {
          const fechaVencimiento = new Date(venta.fecha);
          fechaVencimiento.setDate(fechaVencimiento.getDate() + 30);
          const hoy = new Date();
          const diasVencido = Math.floor((hoy.getTime() - fechaVencimiento.getTime()) / (1000 * 60 * 60 * 24));

          return [
            venta.numero,
            new Date(venta.fecha).toLocaleDateString('es-PE'),
            venta.placa_bus || 'Sin padrón',
            venta.total.toFixed(2),
            fechaVencimiento.toLocaleDateString('es-PE'),
            diasVencido > 0 ? diasVencido : 0,
            ventaPagada(venta) ? 'Pagado' : (diasVencido > 0 ? 'Vencido' : 'Pendiente')
          ];
        });
        nombreArchivo = `reporte_creditos_${fechaInicio}_${fechaFin}.csv`;
        break;

      case 'utilidades':
        headers = ['Producto', 'Cantidad Vendida', 'Monto Ventas', 'Costo Total', 'Utilidad', 'Margen %'];
        datos = productosMasVendidos().map(producto => {
          const prod = productos.find(p => p.id === producto.id);
          const costo = (prod?.precio_compra || 0) * producto.cantidad;
          const utilidad = producto.monto - costo;
          const margen = producto.monto > 0 ? (utilidad / producto.monto) * 100 : 0;

          return [
            producto.nombre,
            producto.cantidad,
            producto.monto.toFixed(2),
            costo.toFixed(2),
            utilidad.toFixed(2),
            margen.toFixed(2)
          ];
        });
        nombreArchivo = `reporte_utilidades_${fechaInicio}_${fechaFin}.csv`;
        break;
    }

    // Generar CSV
    const csvContent = [
      headers.join(','),
      ...datos.map(row => row.map((cell: any) => {
        // Escapar comillas y comas
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    // Descargar archivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', nombreArchivo);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const stats = estadisticasVentas();
  const statsCreditos = estadisticasCreditos();
  const ventasDiarias = ventasPorDia();
  const topProductos = productosMasVendidos();

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Reportes y Análisis</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setVistaActual('dashboard')}
            className={`px-4 py-2 rounded-lg ${vistaActual === 'dashboard' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setVistaActual('ventas')}
            className={`px-4 py-2 rounded-lg ${vistaActual === 'ventas' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Ventas
          </button>
          <button
            onClick={() => setVistaActual('creditos')}
            className={`px-4 py-2 rounded-lg ${vistaActual === 'creditos' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Créditos
          </button>
          <button
            onClick={() => setVistaActual('utilidades')}
            className={`px-4 py-2 rounded-lg ${vistaActual === 'utilidades' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Utilidades
          </button>
        </div>
      </div>

      {/* Filtros de Fecha */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Fecha Inicio</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="w-full p-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Fecha Fin</label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              className="w-full p-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tipo de Pago</label>
            <select
              value={filtroTipoPago}
              onChange={(e) => setFiltroTipoPago(e.target.value)}
              className="w-full p-2 border rounded-lg"
            >
              <option value="todos">Todos</option>
              <option value="contado">Contado</option>
              <option value="credito">Crédito</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => exportarCSV(vistaActual === 'dashboard' ? 'ventas' : vistaActual)}
              className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </button>
          </div>
        </div>
      </div>

      {/* Reporte Diario (PDF, se sube solo a Google Drive) */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex items-center mb-3">
          <FileText className="w-5 h-5 text-blue-600 mr-2" />
          <h3 className="font-semibold text-gray-800">Reporte Diario (PDF)</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Ventas del día, repuestos vendidos, créditos nuevos generados ese día e ingresos
          por cada caja. Se guarda automáticamente en Google Drive — volver a generarlo el
          mismo día actualiza el PDF si hubo ventas nuevas.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">Fecha</label>
            <input
              type="date"
              value={fechaReporteDiario}
              onChange={(e) => setFechaReporteDiario(e.target.value)}
              className="p-2 border rounded-lg"
            />
          </div>
          <button
            onClick={handleGenerarReporteDiario}
            disabled={generandoReporteDiario}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center disabled:opacity-60"
          >
            <FileText className="w-4 h-4 mr-2" />
            {generandoReporteDiario ? 'Generando…' : 'Generar y Descargar Reporte Diario'}
          </button>
        </div>
      </div>

      {/* Vista Dashboard */}
      {vistaActual === 'dashboard' && (
        <div className="space-y-6">
          {/* Métricas Principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Ventas</p>
                  <p className="text-2xl font-bold">S/ {stats.montoTotalVentas.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">{stats.totalVentas} transacciones</p>
                </div>
                <ShoppingCart className="w-8 h-8 text-blue-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Utilidades</p>
                  <p className="text-2xl font-bold text-green-600">S/ {stats.utilidadTotal.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">
                    {stats.montoTotalVentas > 0 ? ((stats.utilidadTotal / stats.montoTotalVentas) * 100).toFixed(1) : 0}% margen
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Deuda Pendiente (hoy)</p>
                  <p className="text-2xl font-bold text-yellow-600">S/ {deudaPendienteReal.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">{busesConDeudaReal} buses con deuda</p>
                </div>
                <CreditCard className="w-8 h-8 text-yellow-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Créditos Vencidos</p>
                  <p className="text-2xl font-bold text-red-600">S/ {statsCreditos.montoVencido.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">{statsCreditos.creditosVencidos} vencidos</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
            </div>
          </div>

          {/* Métricas Adicionales */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-4">Desglose por Tipo de Pago</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Contado ({stats.ventasContado})</span>
                  <span className="font-medium">S/ {stats.montoContado.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-purple-700">Yape ({stats.ventasYape})</span>
                  <span className="font-medium text-purple-700">S/ {stats.montoYape.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Crédito ({stats.ventasCredito})</span>
                  <span className="font-medium">S/ {stats.montoCredito.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2">
                  <div className="flex justify-between font-semibold">
                    <span>Total</span>
                    <span>S/ {stats.montoTotalVentas.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-4">Estado de Créditos</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Total Otorgado</span>
                  <span className="font-medium">S/ {statsCreditos.montoTotalCreditos.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-yellow-600">
                  <span>Pendiente (deuda actual)</span>
                  <span className="font-medium">S/ {deudaPendienteReal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-red-600">
                  <span>Vencido</span>
                  <span className="font-medium">S/ {statsCreditos.montoVencido.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2">
                  <div className="flex justify-between text-green-600 font-semibold">
                    <span>Cobrado en el período</span>
                    <span>S/ {totalCobradoPeriodo.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{pagosCreditoFiltrados.length} pagos registrados</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-4">Indicadores</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Ticket Promedio</span>
                  <span className="font-medium">S/ {stats.ticketPromedio.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Clientes con Crédito</span>
                  <span className="font-medium">{statsCreditos.clientesConCredito}</span>
                </div>
                <div className="flex justify-between">
                  <span>% Créditos Vencidos</span>
                  <span className="font-medium text-red-600">{statsCreditos.porcentajeVencido.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Gráfico de Ventas por Día */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold mb-4">Ventas por Día</h3>
            <div className="space-y-2">
              {ventasDiarias.slice(-10).map((dia, index) => {
                const maxMonto = Math.max(...ventasDiarias.map(d => d.monto));
                const porcentaje = (dia.monto / maxMonto) * 100;
                
                return (
                  <div key={index} className="flex items-center space-x-4">
                    <span className="text-sm w-20">{new Date(dia.fecha).toLocaleDateString('es-PE')}</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                      <div 
                        className="bg-blue-600 h-6 rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${porcentaje}%` }}
                      >
                        <span className="text-white text-xs font-medium">
                          S/ {dia.monto.toFixed(0)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Productos */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold mb-4">Productos Más Vendidos</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Producto</th>
                    <th className="text-right py-2">Cantidad</th>
                    <th className="text-right py-2">Monto</th>
                    <th className="text-right py-2">% del Total</th>
                  </tr>
                </thead>
                <tbody>
                  {topProductos.slice(0, 5).map((producto, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-2">{producto.nombre}</td>
                      <td className="text-right py-2">{producto.cantidad}</td>
                      <td className="text-right py-2">S/ {producto.monto.toFixed(2)}</td>
                      <td className="text-right py-2">
                        {((producto.monto / stats.montoTotalVentas) * 100).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Vista de Ventas */}
      {vistaActual === 'ventas' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold">Reporte Detallado de Ventas</h3>
              <button
                onClick={() => exportarCSV('ventas')}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Número</th>
                  <th className="px-4 py-2 text-left">Fecha</th>
                  <th className="px-4 py-2 text-left">Cliente</th>
                  <th className="px-4 py-2 text-left">Placa Bus</th>
                  <th className="px-4 py-2 text-center">Tipo Pago</th>
                  <th className="px-4 py-2 text-right">Subtotal</th>
                  <th className="px-4 py-2 text-right">Total</th>
                  <th className="px-4 py-2 text-center">Estado</th>
                </tr>
              </thead>
              <tbody>
                {ventasFiltradas.map(venta => {
                  return (
                    <tr key={venta.id} className="border-b">
                      <td className="px-4 py-2 font-mono text-sm">{venta.numero}</td>
                      <td className="px-4 py-2">{new Date(venta.fecha).toLocaleDateString('es-PE')}</td>
                      <td className="px-4 py-2">{venta.cliente_nombre || 'Cliente General'}</td>
                      <td className="px-4 py-2">
                        {venta.placa_bus ? (
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-mono">
                            {venta.placa_bus}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">Sin padrón</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          venta.tipo_pago === 'yape' ? 'bg-purple-100 text-purple-800' :
                          venta.tipo_pago === 'credito' ? 'bg-orange-100 text-orange-800' :
                          venta.tipo_pago === 'mixto' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {venta.tipo_pago === 'efectivo' ? 'contado' : venta.tipo_pago}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">S/ {venta.subtotal.toFixed(2)}</td>
                      <td className="px-4 py-2 text-right font-semibold">S/ {venta.total.toFixed(2)}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          ventaPagada(venta) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {ventaPagada(venta) ? 'Pagado' : 'Pendiente'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td colSpan={4} className="px-4 py-2">Total del Período</td>
                  <td className="px-4 py-2 text-right">
                    S/ {ventasFiltradas.reduce((sum, v) => sum + v.subtotal, 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    S/ {ventasFiltradas.reduce((sum, v) => sum + v.total, 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Vista de Créditos */}
      {vistaActual === 'creditos' && (
        <div className="space-y-6">
          {/* Alertas de Créditos Vencidos */}
          {statsCreditos.creditosVencidos > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
                <div>
                  <h4 className="text-red-800 font-semibold">
                    Atención: {statsCreditos.creditosVencidos} créditos vencidos
                  </h4>
                  <p className="text-red-700">
                    Monto total vencido: S/ {statsCreditos.montoVencido.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">Reporte de Créditos y Déficits</h3>
                <button
                  onClick={() => exportarCSV('creditos')}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar CSV
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Número</th>
                    <th className="px-4 py-2 text-left">Fecha</th>
                    <th className="px-4 py-2 text-left">Placa Bus</th>
                    <th className="px-4 py-2 text-right">Monto</th>
                    <th className="px-4 py-2 text-center">Vencimiento</th>
                    <th className="px-4 py-2 text-center">Días Vencido</th>
                    <th className="px-4 py-2 text-center">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {ventasFiltradas
                    .filter(v => v.tipo_pago === 'credito' || v.tipo_pago === 'mixto')
                    .map(venta => {
                      const fechaVencimiento = new Date(venta.fecha);
                      fechaVencimiento.setDate(fechaVencimiento.getDate() + 30);
                      const hoy = new Date();
                      const diasVencido = Math.floor((hoy.getTime() - fechaVencimiento.getTime()) / (1000 * 60 * 60 * 24));
                      const pagado = ventaPagada(venta);

                      return (
                        <tr key={venta.id} className="border-b">
                          <td className="px-4 py-2 font-mono text-sm">{venta.numero}</td>
                          <td className="px-4 py-2">{new Date(venta.fecha).toLocaleDateString('es-PE')}</td>
                          <td className="px-4 py-2">
                            {venta.placa_bus ? (
                              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-mono">
                                {venta.placa_bus}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right font-semibold">S/ {venta.total.toFixed(2)}</td>
                          <td className="px-4 py-2 text-center">{fechaVencimiento.toLocaleDateString('es-PE')}</td>
                          <td className="px-4 py-2 text-center">
                            {diasVencido > 0 && !pagado ? (
                              <span className="text-red-600 font-semibold">{diasVencido}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs ${
                              pagado ? 'bg-green-100 text-green-800' :
                              diasVencido > 0 ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {pagado ? 'Pagado' : diasVencido > 0 ? 'Vencido' : 'Pendiente'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Vista de Utilidades */}
      {vistaActual === 'utilidades' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold">Análisis de Utilidades por Producto</h3>
              <button
                onClick={() => exportarCSV('utilidades')}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Producto</th>
                  <th className="px-4 py-2 text-right">Cantidad</th>
                  <th className="px-4 py-2 text-right">Ventas</th>
                  <th className="px-4 py-2 text-right">Costo</th>
                  <th className="px-4 py-2 text-right">Utilidad</th>
                  <th className="px-4 py-2 text-right">Margen %</th>
                </tr>
              </thead>
              <tbody>
                {topProductos.map((producto, index) => {
                  const prod = productos.find(p => p.id === producto.id);
                  const costo = (prod?.precio_compra || 0) * producto.cantidad;
                  const utilidad = producto.monto - costo;
                  const margen = producto.monto > 0 ? (utilidad / producto.monto) * 100 : 0;

                  return (
                    <tr key={index} className="border-b">
                      <td className="px-4 py-2">{producto.nombre}</td>
                      <td className="px-4 py-2 text-right">{producto.cantidad}</td>
                      <td className="px-4 py-2 text-right">S/ {producto.monto.toFixed(2)}</td>
                      <td className="px-4 py-2 text-right">S/ {costo.toFixed(2)}</td>
                      <td className="px-4 py-2 text-right">
                        <span className={utilidad >= 0 ? 'text-green-600' : 'text-red-600'}>
                          S/ {utilidad.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <span className={margen >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {margen.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td className="px-4 py-2">Total</td>
                  <td className="px-4 py-2 text-right">
                    {topProductos.reduce((sum, p) => sum + p.cantidad, 0)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    S/ {topProductos.reduce((sum, p) => sum + p.monto, 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    S/ {topProductos.reduce((sum, p) => {
                      const prod = productos.find(pr => pr.id === p.id);
                      return sum + ((prod?.precio_compra || 0) * p.cantidad);
                    }, 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-right text-green-600">
                    S/ {stats.utilidadTotal.toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-right text-green-600">
                    {stats.montoTotalVentas > 0 ? ((stats.utilidadTotal / stats.montoTotalVentas) * 100).toFixed(1) : 0}%
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportesCompleto;
